import { BASE_SETTING_PRIO } from 'api/digitalTwin/digitalTwinConstants'
import type { CreateSettingData } from '../DigitalTwinSettingDialogContent/DigitalTwinSettingDialogContent'
import type { SettingFormMode } from './settingFormMode.types'

type SettingEditorStateParams = {
  /** The setting the editor was opened on. */
  setting: CreateSettingData
  /** The copy the user is making of `setting`, as the form built it; null while they work on `setting` itself. */
  copy: CreateSettingData | null
  isCollectedSettings: boolean
}

type SettingEditorState = {
  mode: SettingFormMode
  isEventSetting: boolean
  isBaseSettingInCollectedSettings: boolean
  /** The form shows the setting but cannot change it. */
  isLocked: boolean
  isTimeSeries: boolean
}

/**
 * What the setting editor allows for a setting. A copy is a new setting: it modifies nothing, and a
 * copy of an event-linked setting is no longer tied to the event, so it can be edited. A base setting
 * opened in collected settings stays read-only either way.
 */
export function getSettingEditorState({
  setting,
  copy,
  isCollectedSettings,
}: SettingEditorStateParams): SettingEditorState {
  const isEventSetting = !!setting.operational_event
  const isBaseSettingInCollectedSettings = isCollectedSettings && setting.priority === BASE_SETTING_PRIO.priority_level
  const mode = getSettingFormMode(setting, copy)

  return {
    mode,
    isEventSetting,
    isBaseSettingInCollectedSettings,
    isLocked: (isEventSetting && mode !== 'copy') || isBaseSettingInCollectedSettings,
    isTimeSeries: (copy ?? setting).isTimeSeries,
  }
}

function getSettingFormMode(setting: CreateSettingData, copy: CreateSettingData | null): SettingFormMode {
  if (copy) {
    return 'copy'
  }
  return setting.id !== undefined ? 'edit' : 'create'
}
